import React, { useState, useEffect } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useMediaQuery } from 'react-responsive';

import { useUserContext } from '@nexus/fff-utilities';
import useEmailPreferences from './hooks/useEmailPreferences';
import Header from './Header';
import OptionList from './OptionList';
import { options } from './options';
import FormButtons from './FormButtons';
import Toast from '../../components/Toast';
import Layout from '../../components/Layout';
import { isFormDisabled } from './helpers';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import './styles.css';

const EmailPreferences = ({ ldClient }) => {
  const [step, setStep] = useState(0);
  const [currentPreference, setCurrentPreference] = useState(null);
  const [emailPreferences, updatePreference] = useEmailPreferences();
  const [showModal, setShowModal] = useState({ isOpen: false, hasErrors: false });
  const { userInfo } = useUserContext();
  useLaunchDarkly(ldClient, userInfo);
  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });
  let noteStyle = (step === 0)
    ? { margin: '4px 0 30px 0' }
    : { margin: '24px 0 36px 0' };
  noteStyle = isMobile ? noteStyle : {};
  const lastDividerStyle = step === 0 ? { margin: '24px 0' } : { margin: '12px 0 24px' };
  const cancelSubscriptionChange = () => {
    setCurrentPreference(emailPreferences.data.optionEmailPreferences);
    setStep(0);
  };
  const nextStep = () => setStep(1);
  const editOrCancel = step === 0 ? nextStep : cancelSubscriptionChange;
  const savePreference = () => {
    updatePreference(currentPreference)
      .then(() => {
        setShowModal({ isOpen: true, hasErrors: false });
        setStep(0);
      })
      .catch((error) => {
        setShowModal({ isOpen: true, hasErrors: true });
        setStep(0);
      });
  };
  const onToastClose = () => {
    setShowModal((modalState) => ({ ...modalState, isOpen: false }));
  };

  useEffect(() => {
    const { data } = emailPreferences;
    if (data && data.optionEmailPreferences) {
      setCurrentPreference(data.optionEmailPreferences);
    }
  }, [emailPreferences]);

  return (
    <Layout isLoading={false} redirectIfNoAuth={false}>
      <div className="layout-container">
        <div className="preferences-container">
          <Header currentStep={step} onActionClick={editOrCancel} />
          <div className="preferences-body">
            {step === 0 && (
              <>
                {Object.keys(emailPreferences.data).length > 0
                && emailPreferences.data.optionEmailPreferences && (
                  <h3 className="preferences-body-title">
                    {options[emailPreferences.data.optionEmailPreferences - 1].label}
                  </h3>
                )}
              </>
            )}
            {step === 1 && (
              <OptionList
                selectOption={(option) => setCurrentPreference(option)}
                currentPreference={currentPreference}
              />
            )}
            <p
              className="note"
              style={noteStyle}
            >
              Please Note: Managing your preferences does not impact
              transactional emails such as payment invoices,
              upcoming billing, shipping or delivery notifications.
              You will continue to receive these emails.
            </p>
          </div>
          {step === 1 && (
            <FormButtons
              onSave={savePreference}
              onCancel={cancelSubscriptionChange}
              disabled={isFormDisabled(emailPreferences, currentPreference)}
            />
          )}
          <div id="last" className="divider" style={lastDividerStyle} />
          <Toast
            isOpen={showModal.isOpen}
            onRequestClose={onToastClose}
            hasErrors={showModal.hasErrors}
            title={`Email Preferences${showModal.hasErrors ? ' not ' : ' '}Updated`}
            message={
              showModal.hasErrors ? (
                'Ops! Something went wrong...'
              ) : (
                'Your notifications change has been successfully saved.'
              )
            }
          />
        </div>
      </div>
    </Layout>
  );
};

export default withLDConsumer()(EmailPreferences);
